import React, { useState, useEffect, useRef } from 'react';
import { TargetInfo } from '@/game/systems/combat/TargetingSystem';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { useRenderCount } from '@/game/diagnostics/useRenderCount';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  Z,
  DURATION,
  EASING,
  FONTS,
  classColor,
  classIcon,
  formatCallingName,
} from '../theme';
import { useHudTomeVibe } from '../HUD/useHudTomeVibe';
import { useCurrentZoneId } from '../hooks/useCurrentZoneId';
import { openInspect } from '../Windows/Social/inspect/inspectTarget';
import './TargetFrame.css';
import { ActiveStatusIndex, type ActiveStatusEntry } from '../../systems/combat/ActiveStatusIndex';
import { Tooltip } from '../components/Tooltip';
import {
  createStatusTooltip,
  resolveStatusPresentation,
} from '../status/StatusPresentation';
import { DevConfig } from '../../../config/environment/devConfig';
import { useHudTopLayout } from '../HUD/hudTopLayout';
import type { HudTopLayoutResult } from '../HUD/hudTopLayout';
import {
  useHudCompactLayout,
  compactTargetPanelMaxHeight,
} from '../HUD/hudCompactLayout';
import {
  HUD_OCCLUSION_TARGET_PANEL,
  clearHudOcclusionZone,
  setHudOcclusionZone,
} from '../../rendering/hudOcclusionZones';

// Health-state hues stay literal: they are functional (asserted verbatim by
// TargetInfoPanel.test.tsx), not part of the zone palette.
const HEALTH_GOOD = '#4CAF50';
const HEALTH_MID = '#FFC107';
const HEALTH_LOW = '#F44336';

// Plan 23 row 34 (lane BM): the frame wears the tome look the HUD uses — the
// zone's --tome-hud-* tokens (dark ground, gilt rule, parchment ink), the
// same treatment as the map pill and the party invite. Placement comes from
// useHudTopLayout (U2): top/left/maxWidth/minWidth/maxHeight at render time.
// `boxSizing: border-box` keeps the maxHeight cap honest on short screens.
const panelContainerBaseStyle: React.CSSProperties = {
  position: 'fixed',
  boxSizing: 'border-box',
  background: 'linear-gradient(180deg, var(--tome-hud-ground-hi), var(--tome-hud-ground))',
  border: '1px solid var(--tome-hud-rule)',
  borderRadius: 'var(--tome-cut-2)',
  boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.4)',
  color: 'var(--tome-hud-ink)',
  padding: SPACE.lg,
  minWidth: 250,
  maxWidth: 300,
  pointerEvents: 'none',
  zIndex: Z.hud,
  transition: `opacity ${DURATION.slow} ${EASING.inOut}`,
};
const panelContainerCompactStyle: React.CSSProperties = { ...panelContainerBaseStyle, padding: SPACE.md };

// Name: display font, legibility floor FONT_SIZE.xl in both layouts (W2a).
const targetNameStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xl,
  fontWeight: FONT_WEIGHT.bold,
  fontFamily: FONTS.display,
  color: 'var(--tome-hud-ink)',
  lineHeight: 1.15,
  wordBreak: 'break-word',
};

// Distance chip is IN FLOW (U2): a right-aligned row after the content can
// never overlap the status chips. Shown for hostile targets only — range
// matters when you mean to hit something, not when you mean to help.
const distanceRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', marginTop: SPACE.sm };
const distanceRowCompactStyle: React.CSSProperties = { ...distanceRowStyle, marginTop: SPACE.xs };

const statusChipStyle: React.CSSProperties = {
  position: 'relative',
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: RADII.md,
  background: 'rgba(0, 0, 0, 0.35)',
  color: COLORS.textPrimary,
  cursor: 'help',
  fontSize: FONT_SIZE.lg,
};

const statusTimerStyle: React.CSSProperties = {
  position: 'absolute',
  right: -3,
  bottom: -3,
  padding: '0 2px',
  borderRadius: RADII.sm,
  background: 'rgba(0,0,0,0.85)',
  color: '#fff',
  fontSize: 8,
  lineHeight: 1.2,
};

// Mob type to emoji/icon mapping
const MOB_ICONS: Record<string, string> = {
  'pig': '🐷',
  'slime': '🟢',
  'training_dummy': '🎯',
  'goblin': '👺',
  'skeleton': '💀',
  'wolf': '🐺',
  'default': '👾'
};

import { EventBus, EVENTS } from '../../../shared/EventBus';

/** Target-frame disposition chip (plan 23 row 34): a player you can help
 *  (pvpResolution.canHelp) is an Ally, a player PvP lets you hurt is
 *  Hostile; mobs keep the mob glyph; NPCs are NPCs. */
export function targetTypePresentation(type: string, isNpc = false): { icon: string; label: string; tone: string } {
  if (isNpc || type === 'npc') return { icon: '💬', label: 'NPC', tone: 'var(--tome-info)' };
  if (type === 'ally') return { icon: '🛡️', label: 'Ally', tone: 'var(--tome-moss-soft)' };
  if (type === 'player') return { icon: '⚔️', label: 'Hostile', tone: 'var(--tome-danger)' };
  return { icon: MOB_ICONS[type] || MOB_ICONS.default, label: 'Enemy', tone: 'var(--tome-danger)' };
}

/** One vital: a labelled bar whose fill moves by transform (never width). */
const Vital: React.FC<{ kind: 'hp' | 'mp'; value: number; max: number; color: string; compact: boolean }> = ({
  kind, value, max, color, compact,
}) => {
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div className="target-frame-vital" data-kind={kind} data-compact={compact ? 'true' : 'false'}>
      <div className="target-frame-bar" role="meter" aria-label={kind === 'hp' ? 'Health' : 'Mana'}
        aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.round(value)}>
        <div className="target-frame-bar-fill" style={{ backgroundColor: color, transform: `scaleX(${ratio})` }} />
        <div className="target-frame-bar-text">{Math.round(value)}/{Math.round(max)}</div>
      </div>
    </div>
  );
};

export const TargetInfoPanel: React.FC = () => {
  useRenderCount('TargetInfoPanel');
  const [targetInfo, setTargetInfo] = useState<TargetInfo | null>(null);
  const [visible, setVisible] = useState(false);
  const statuses = useTargetStatuses(targetInfo?.id);
  const layout = useHudTopLayout(true, DevConfig?.systems?.debugOverlay ?? false);
  // Compact landscape (W2a): cap the frame above the scaled action band so it
  // can never reach the chat peek / hotbar band at short heights. Heights
  // > 450 keep the accepted desktop maxHeight untouched.
  const compact = useHudCompactLayout({ bannerClearanceTop: layout.bannerClearanceTop });
  const targetPanelMaxHeight = compact.isCompact
    ? Math.min(
        layout.targetPanelMaxHeight,
        compactTargetPanelMaxHeight(compact, layout.targetPanelTop),
      )
    : layout.targetPanelMaxHeight;
  const panelRef = useRef<HTMLDivElement | null>(null);

  // W1a (2026-09-28) — publish the frame rect for world-prompt avoidance.
  // Layout-time only (one measure per layout change + ResizeObserver), never
  // per frame; see the hook below and src/game/rendering/hudOcclusionZones.ts.
  useHudOcclusionPublish(panelRef, visible, targetInfo?.id, layout, targetPanelMaxHeight);

  // Selection changes and display refreshes have different combat semantics.
  useEffect(() => {
    const handleTargetChanged = (info: TargetInfo | null) => {
      setTargetInfo(info);
      setVisible(!!info);
    };

    const eventBus = EventBus.getInstance();
    const unsubscribe = eventBus.on(EVENTS.TARGET_CHANGED, handleTargetChanged);
    const unsubscribeInfo = eventBus.on(EVENTS.TARGET_INFO_UPDATED, info => {
      // A late update must never resurrect a cleared or superseded selection.
      setTargetInfo(current => current?.id === info.id ? info : current);
    });
    // A remounted panel may have missed selection while it was absent.
    handleTargetChanged(ServiceRegistry.getInstance().get('TargetingSystem', { optional: true })?.getTargetInfo() ?? null);

    return () => {
      unsubscribe();
      unsubscribeInfo();
    };
  }, []);

  const tome = useHudTomeVibe(useCurrentZoneId());

  if (!targetInfo) {
    return null;
  }

  const healthPercent = targetInfo.maxHealth > 0 ? (targetInfo.health / targetInfo.maxHealth) * 100 : 0;
  const healthColor = healthPercent > 50 ? HEALTH_GOOD : healthPercent > 25 ? HEALTH_MID : HEALTH_LOW;
  const isPlayer = targetInfo.type === 'ally' || targetInfo.type === 'player';
  const disposition = targetTypePresentation(targetInfo.type, targetInfo.isNpc);
  const hostile = !targetInfo.isNpc && targetInfo.type !== 'ally';
  const calling = isPlayer && targetInfo.calling ? targetInfo.calling : null;
  // Players wear their calling's glyph and a class-coloured calling line; the
  // name stays parchment ink (a red class colour on the name read as "hostile").
  const glyph = calling ? classIcon(calling) : disposition.icon;
  const callingColor = calling ? classColor(calling) : undefined;
  const compactLayout = compact.isCompact;

  return (
    <div
      ref={panelRef}
      data-testid="target-info-panel"
      data-compact={compactLayout ? 'true' : 'false'}
      data-disposition={targetInfo.isNpc ? 'npc' : targetInfo.type}
      className={`target-frame ${tome.className}`}
      style={{
        ...tome.style,
        ...(compactLayout ? panelContainerCompactStyle : panelContainerBaseStyle),
        top: layout.targetPanelTop,
        left: layout.targetPanelLeft,
        maxWidth: layout.targetPanelMaxWidth,
        minWidth: layout.targetPanelMinWidth,
        maxHeight: targetPanelMaxHeight,
        // Bounds the frame on short screens. pointerEvents stay 'none' on the
        // container per the accepted contract; only the Inspect button and
        // the status chips take the pointer.
        overflowY: 'auto',
        opacity: visible ? 1 : 0,
      }}
    >
      <div className="target-frame-head">
        <span className="target-frame-glyph" aria-hidden="true">{glyph}</span>
        <div className="target-frame-id">
          <div style={targetNameStyle}>{targetInfo.name}</div>
          <div className="target-frame-sub">
            <span>Level {targetInfo.level}</span>
            {calling && <span className="target-frame-calling" style={{ '--target-calling': callingColor } as React.CSSProperties}>
              {formatCallingName(calling)}
            </span>}
            <span className="target-frame-chip" data-testid="target-disposition"
              style={{ '--target-chip-tone': disposition.tone } as React.CSSProperties}>
              {isPlayer && <span aria-hidden="true">{disposition.icon}</span>}
              <span>{disposition.label}</span>
            </span>
          </div>
        </div>
      </div>

      <Vital kind="hp" value={targetInfo.health} max={targetInfo.maxHealth} color={healthColor} compact={compactLayout} />
      {isPlayer && typeof targetInfo.maxMana === 'number' && targetInfo.maxMana > 0 && (
        <Vital kind="mp" value={targetInfo.mana ?? 0} max={targetInfo.maxMana} color="#3d7fd9" compact={compactLayout} />
      )}

      {statuses.length > 0 && (
        <div aria-label="Target status effects" className="target-frame-statuses" data-compact={compactLayout ? 'true' : 'false'}>
          {statuses.map(([effectId, entry]) => {
            const remaining = Math.max(0, entry.expiresAt - Date.now());
            const presentation = resolveStatusPresentation(effectId, {
              data: entry.data,
              sourceName: resolveStatusSourceName(entry.sourceId),
            });
            const colorHex = `#${presentation.color.toString(16).padStart(6, '0')}`;
            return (
              <Tooltip
                key={effectId}
                data={createStatusTooltip(presentation, remaining, entry.stacks ?? 1)}
                position="bottom"
              >
                <div
                  aria-label={`${presentation.name} on target`}
                  data-effect-id={effectId}
                  data-status-disposition={presentation.disposition}
                  tabIndex={0}
                  style={{
                    ...statusChipStyle,
                    border: `1px solid ${colorHex}`,
                    boxShadow: `0 0 7px ${colorHex}55`,
                  }}
                >
                  {presentation.icon.startsWith('/')
                    ? <img src={presentation.icon} alt={presentation.name} draggable={false}
                        style={{ width: 22, height: 22, objectFit: 'contain' }} />
                    : presentation.icon}
                  {remaining > 0 && <span style={statusTimerStyle}>{compactDuration(remaining)}</span>}
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      {isPlayer && (
        <div className="target-frame-actions">
          <button type="button" className="target-frame-inspect" data-testid="target-inspect"
            onClick={() => openInspect({ sessionId: targetInfo.id, name: targetInfo.name })}>
            Inspect
          </button>
        </div>
      )}

      {hostile && targetInfo.distance !== undefined && (
        <div style={compactLayout ? distanceRowCompactStyle : distanceRowStyle}>
          <span className="target-frame-distance">{Math.round(targetInfo.distance)}m</span>
        </div>
      )}
    </div>
  );
};

function useTargetStatuses(
  targetId: string | undefined,
): Array<[string, ActiveStatusEntry]> {
  const [statuses, setStatuses] = useState<Array<[string, ActiveStatusEntry]>>([]);

  useEffect(() => {
    if (!targetId) {
      setStatuses([]);
      return;
    }
    const index = ActiveStatusIndex.getInstance();
    const refresh = () => setStatuses(index.getStatusEntries(targetId));
    refresh();
    const unsubscribe = index.subscribe(changedTargetId => {
      if (changedTargetId === targetId) refresh();
    });
    const timer = setInterval(refresh, 250);
    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [targetId]);

  return statuses;
}

function compactDuration(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return seconds >= 60 ? `${Math.ceil(seconds / 60)}m` : `${seconds}s`;
}

function resolveStatusSourceName(sourceId: string | undefined): string | undefined {
  if (!sourceId) return undefined;
  const room = (globalThis as any).nm?.getRoom?.();
  return room?.state?.players?.get?.(sourceId)?.heroName;
}

/**
 * W1a (2026-09-28) — keeps the HUD occlusion store in sync with this panel's
 * real viewport rect so world prompts can avoid rendering behind it.
 *
 * Measurement happens ONLY on layout changes: once per effect run, plus
 * ResizeObserver callbacks (panel growth/shrink). The per-tick Pixi prompt
 * clamp reads the cached store snapshot — there is no per-frame
 * `getBoundingClientRect()` anywhere in the render path.
 *
 * Clears the zone when the panel is hidden or unmounted so prompts stop
 * avoiding a surface that is no longer on screen.
 */
function useHudOcclusionPublish(
  panelRef: React.RefObject<HTMLDivElement | null>,
  visible: boolean,
  contentKey: string | undefined,
  layout: HudTopLayoutResult,
  targetPanelMaxHeight: number,
): void {
  useEffect(() => {
    const element = panelRef.current;
    if (!element || !visible) {
      clearHudOcclusionZone(HUD_OCCLUSION_TARGET_PANEL);
      return;
    }

    const publish = (): void => {
      const rect = element.getBoundingClientRect();
      setHudOcclusionZone(HUD_OCCLUSION_TARGET_PANEL, {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      });
    };

    // Initial publish for this layout; ResizeObserver covers later resizes.
    publish();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish);
      observer.observe(element);
    }

    return () => {
      observer?.disconnect();
      clearHudOcclusionZone(HUD_OCCLUSION_TARGET_PANEL);
    };
    // Layout values reposition/resize the frame; contentKey catches content
    // swaps between measured layout updates, and the ResizeObserver catches
    // in-place growth (status chips, wrapped names, scrolling).
  }, [
    panelRef,
    visible,
    contentKey,
    layout.targetPanelTop,
    layout.targetPanelLeft,
    layout.targetPanelMaxWidth,
    layout.targetPanelMinWidth,
    // Effective (compact-capped) maxHeight — the cap moves the frame's bottom
    // edge on short viewports without changing its top/left.
    targetPanelMaxHeight,
  ]);
}
